import React from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router';
import Login from './UI/Pages/Login/login.page';
import SetUpAccount from './UI/Pages/SetUpAccount/setUpAccount';
import VerifyCode from './UI/Pages/VerifyCode/verifyCode.page';
import ForgotPassword from './UI/Pages/ForgotPassword/forgotPassword';
import Account from './UI/Pages/AccountPage/account.page';
import PacsSync from './UI/Pages/AnalyticsPage1/analytics.page';
import Support from './UI/Pages/SupportPage/support.page';
import ProfilePage from './UI/Pages/AccountPage/account.page';
import PatientManagement from './UI/Pages/PatientManagement/patient_management.page';
import ExamManagement from './UI/Pages/ExamManagement/exam_management.page';
import StudyInbox from './UI/Pages/StudyInbox/study_inbox.page';
import RealTimeInferencingV3 from './UI/Pages/RealtimeInferencingPageV3/realtime-inferencing_v3.page';
import RealtimeExamPage from './UI/Pages/RealtimeExamPage/realtime-exam.page';
import BlindSweep from './UI/Pages/BlindSweep/blind_sweep.page';
import BlindLoops from './UI/Pages/BlindLoops/BlindLoops';
import ResetPassword from './UI/Pages/ResetPassword/resetPassword';
import ForcePasswordChange from './UI/Pages/ForcePasswordChange/forcePasswordChange';
import ApiAsService from './UI/Pages/ApiAsService/apiasservice.page';
import ArchitectureDiagram from './UI/Pages/ArchitecturePage/architecture';
import AboutPage from './AboutPage';
import AiAccelerate from './UI/Pages/AiAccelerate/AiAccelerate';
import PatientTimeline from './UI/Pages/PatientManagement/PatientTimeline';
import TermsPage from './TermsPage';
import TestPage from './UI/Pages/TestPage/test';
import DevicesPage from './UI/Pages/DevicesPage/devices.page';
import WorklistManagementPage from './UI/Pages/WorklistManagement/worklist-management.page';
import OrganizationPage from './UI/Pages/OrganizationPage/organization.page';
import OrgStructurePage from './UI/Pages/OrgStructure/org_structure.page';
import RolesPermissionsPage from './UI/Pages/RolesPermissions/roles_permissions.page';
import TwoFactorSetupPage from './UI/Pages/TwoFactorSetup/TwoFactorSetup.page';
import FeatureFlagsDebug from './UI/Pages/FeatureFlagsDebug/feature_flags_debug.page';
import BsExamAnalyticsPage from './UI/Pages/BsExamAnalyticsPage/bs-exam-analytics.page';
import QuickInferencePage from './UI/Pages/QuickInferencePage/quick-inference.page';
import QuickInferenceViewerPage from './UI/Pages/QuickInferencePage/quick-inference-viewer.page';
import HomePage from './UI/Pages/HomePage/home.page';
import PredictionPerformancePage from './UI/Pages/PredictionPerformancePage/prediction-performance.page';
import SurveysPage from './UI/Pages/Surveys/surveys.page';
import SurveyDetailPage from './UI/Pages/Surveys/survey-detail.page';
import PlatformModeRoute from './Components/PlatformModeRoute';
import SchedulingPage from './UI/Pages/SchedulingPage/PatientScheduling';
import { AddPatientPage } from './UI/Pages/PatientManagement/AddPatientPage';
import { EditPatientPage } from './UI/Pages/PatientManagement/EditPatientPage';
import { NewCaseWizard } from './UI/ComponnentsUI/NewCaseReverseUpload/NewCaseOneStep';
import AuditTrailPage from './UI/Pages/AuditTrail/audit-trail.page';

function QuickInferenceLegacyRedirect() {
  const { sessionId } = useParams<{ sessionId: string }>();
  return <Navigate to={sessionId ? `/playground/${sessionId}` : '/playground'} replace />;
}

// Layer-1 page gates: the effective "entity.action"(s) that reveal each page.
// OR semantics (any one is enough). Omit `requiredPermissions` for pages open to
// any signed-in user (dashboard, self/account, surveys, api-as-service). The
// backend enforces every action regardless of what the nav/route shows.
const VIEW_PATIENTS = ['patient.read'];
const VIEW_EXAMS = ['study.read'];
const ACQUIRE_EXAMS = ['study.acquire'];
const VIEW_PACS = ['pacs_device.read'];
const VIEW_SCHEDULING = ['appointment.read'];
const VIEW_ORG_ADMIN = ['user.read', 'org_unit.read', 'role.read', 'pacs_device.manage'];

export const ProtectedPages = [
  {
    path: '/test',
    element: <TestPage />,
  },
  {
    path: '/home',
    element: <HomePage />,
  },
  {
    path: '/new-case',
    element: <NewCaseWizard />,
    requiredPermissions: ACQUIRE_EXAMS,
  },
  {
    path: '/api-as-service',
    element: <ApiAsService />,
  },
  {
    path:'/Scheduling',
    element: <SchedulingPage />,
    requiredPermissions: VIEW_SCHEDULING,
  },
  // {
  //   path: '/worklist',
  //   element: (
  //     <PlatformModeRoute allowedModes={['full']}>
  //       <WorklistManagementPage />
  //     </PlatformModeRoute>
  //   ),
  //   requiredPermissions: VIEW_SCHEDULING,
  // },
  {
    path: '/patients/new',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <AddPatientPage />
      </PlatformModeRoute>
    ),
    requiredPermissions: ['patient.create'],
  },
  {
    path: '/patients/:id/edit',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <EditPatientPage />
      </PlatformModeRoute>
    ),
    requiredPermissions: ['patient.write'],
  },
  {
    path: '/prediction-performance',
    element: <PredictionPerformancePage />,
    requiredPermissions: ['audit_log.read'],
  },
  {
    path: '/audit-trail',
    element: <AuditTrailPage />,
    requiredPermissions: ['audit_log.read'],
  },
  {
    path: '/surveys',
    element: <SurveysPage />,
  },
  {
    path: '/surveys/:surveyId',
    element: <SurveyDetailPage />,
  },
  ...(process.env.NODE_ENV === 'development' ? [{
    path: '/archi',
    element: <ArchitectureDiagram />,
  }] : []),
  {
    path: '/about',
    element: <AboutPage />,
  },
  {
    path: '/terms',
    element: <TermsPage />,
  },
  {
    path: '/profile/:uid',
    element: <ProfilePage />,
  },
  {
    path: '/sync',
    element: <PacsSync />,
    requiredPermissions: ['pacs_device.sync'],
  },

  {
    path: '/realtime',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <RealTimeInferencingV3 />
      </PlatformModeRoute>
    ),
    requiredPermissions: ACQUIRE_EXAMS,
  },
  {
    path: '/realtimeexam',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <RealtimeExamPage />
      </PlatformModeRoute>
    ),
    requiredPermissions: ACQUIRE_EXAMS,
  },
  {
    path: '/exams',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <ExamManagement />
      </PlatformModeRoute>
    ),
    requiredPermissions: VIEW_EXAMS,
  },
  {
    path: '/pacs-inbox',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <StudyInbox />
      </PlatformModeRoute>
    ),
    requiredPermissions: VIEW_PACS,
  },
  {
    path: '/patients',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <PatientManagement />
      </PlatformModeRoute>
    ),
    requiredPermissions: VIEW_PATIENTS,
  },
  {
    path: '/patients/:id',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <PatientTimeline />
      </PlatformModeRoute>
    ),
    requiredPermissions: VIEW_PATIENTS,
  },
  {
    path: '/sweep',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <BlindSweep />
      </PlatformModeRoute>
    ),
    requiredPermissions: VIEW_EXAMS,
  },
  {
    path: '/sweep/viewer',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <BlindLoops />
      </PlatformModeRoute>
    ),
    requiredPermissions: VIEW_EXAMS,
  },
  {
    path: '/exam/viewer',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <AiAccelerate />
      </PlatformModeRoute>
    ),
    requiredPermissions: VIEW_EXAMS,
  },

  {
    path: '/organization',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <OrganizationPage />
      </PlatformModeRoute>
    ),
    requiredPermissions: VIEW_ORG_ADMIN,
  },
  {
    path: '/org-structure',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <OrgStructurePage />
      </PlatformModeRoute>
    ),
    requiredPermissions: ['org_unit.read'],
  },
  {
    path: '/roles-permissions',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <RolesPermissionsPage />
      </PlatformModeRoute>
    ),
    requiredPermissions: ['role.read'],
  },
  {
    path: '/2fa-setup',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <TwoFactorSetupPage />
      </PlatformModeRoute>
    ),
  },
  {
    path: '/feature-flags-debug',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <FeatureFlagsDebug />
      </PlatformModeRoute>
    ),
    requiredPermissions: ['role.manage'],
  },
  {
    path: '/exam-analytics',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <BsExamAnalyticsPage />
      </PlatformModeRoute>
    ),
  },
  {
    path: '/playground',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <QuickInferencePage />
      </PlatformModeRoute>
    ),
  },
  {
    path: '/playground/:sessionId',
    element: (
      <PlatformModeRoute allowedModes={['full']}>
        <QuickInferenceViewerPage />
      </PlatformModeRoute>
    ),
  },
  {
    path: '/quick-inference',
    element: <Navigate to="/playground" replace />,
  },
  {
    path: '/quick-inference/:sessionId',
    element: <QuickInferenceLegacyRedirect />,
  },
];

const Pages = () => {
  return (
    <Routes>
      <Route path="/about" element={<AboutPage />} />
      <Route path="/api-as-service" element={<ApiAsService />} />
      {process.env.NODE_ENV === 'development' && (
        <Route path="/archi" element={<ArchitectureDiagram />} />
      )}
      <Route path="/login" element={<Login />} />
      <Route path="/test" element={<TestPage />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/force-password-change" element={<ForcePasswordChange />} />
      <Route path="/set-up-account" element={<SetUpAccount />} />
      <Route path="/activate" element={<VerifyCode />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/account" element={<Account />} />
      {/* <Route path="/report" element={<Report />} /> */}

      <Route
        path="/realtimeexam"
        element={
          <PlatformModeRoute allowedModes={['full']}>
            <RealtimeExamPage />
          </PlatformModeRoute>
        }
      />
      <Route
        path="/exams"
        element={
          <PlatformModeRoute allowedModes={['full']}>
            <ExamManagement />
          </PlatformModeRoute>
        }
      />
      <Route
        path="/pacs-inbox"
        element={
          <PlatformModeRoute allowedModes={['full']}>
            <StudyInbox />
          </PlatformModeRoute>
        }
      />
      <Route
        path="/devices"
        element={
          <PlatformModeRoute allowedModes={['full']}>
            <DevicesPage />
          </PlatformModeRoute>
        }
      />
      <Route
        path="/organization"
        element={
          <PlatformModeRoute allowedModes={['full']}>
            <OrganizationPage />
          </PlatformModeRoute>
        }
      />
      <Route
        path="/org-structure"
        element={
          <PlatformModeRoute allowedModes={['full']}>
            <OrgStructurePage />
          </PlatformModeRoute>
        }
      />
      <Route
        path="/roles-permissions"
        element={
          <PlatformModeRoute allowedModes={['full']}>
            <RolesPermissionsPage />
          </PlatformModeRoute>
        }
      />
      <Route
        path="/2fa-setup"
        element={
          <PlatformModeRoute allowedModes={['full']}>
            <TwoFactorSetupPage />
          </PlatformModeRoute>
        }
      />
      <Route
        path="/feature-flags-debug"
        element={
          <PlatformModeRoute allowedModes={['full']}>
            <FeatureFlagsDebug />
          </PlatformModeRoute>
        }
      />
      <Route
        path="/patients"
        element={
          <PlatformModeRoute allowedModes={['full']}>
            <PatientManagement />
          </PlatformModeRoute>
        }
      />
      <Route
        path="/Scheduling"
        element={
          <PlatformModeRoute allowedModes={['full']}>
            <SchedulingPage />
          </PlatformModeRoute>
        }
      />
      <Route
        path="/patients/:id"
        element={
          <PlatformModeRoute allowedModes={['full']}>
            <PatientTimeline />
          </PlatformModeRoute>
        }
      />
      <Route
        path="/sweep"
        element={
          <PlatformModeRoute allowedModes={['full']}>
            <BlindSweep />
          </PlatformModeRoute>
        }
      />
      <Route
        path="/sweep/viewer"
        element={
          <PlatformModeRoute allowedModes={['full']}>
            <BlindLoops />
          </PlatformModeRoute>
        }
      />
      <Route path="/support" element={<Support />} />
      <Route path="/account" element={<Account />} />
    </Routes>
  );
};

export default Pages;
